/**
 * Documentation Search Index Service
 *
 * Uses MiniSearch for fast client-side full-text search across
 * all EnergyPlus documentation.
 */

import MiniSearch from 'minisearch';
import type { IDDSchema } from '@/idd/types';
import type { SearchDocument, DocsSearchResult, EngineeringDocument } from '../types';

let searchIndex: MiniSearch<SearchDocument> | null = null;
let isInitialized = false;

/**
 * Initialize the search index with IDD schema and engineering content
 */
export async function initializeSearchIndex(
  iddSchema: IDDSchema,
  engineeringDocs: EngineeringDocument[] = []
): Promise<number> {
  searchIndex = new MiniSearch<SearchDocument>({
    fields: ['title', 'content', 'objectType', 'group'],
    storeFields: ['objectType', 'fieldName', 'type', 'title', 'group', 'content'],
    searchOptions: {
      boost: { title: 2, objectType: 1.5 },
      fuzzy: 0.2,
      prefix: true,
    },
  });

  const documents: SearchDocument[] = [];
  let docId = 0;

  // Index all IDD objects
  for (const [, objType] of iddSchema.objectTypes) {
    // Object-level document
    documents.push({
      id: String(docId++),
      objectType: objType.name,
      type: 'object',
      title: objType.name,
      content: objType.memo || '',
      group: objType.group,
    });

    // Field-level documents
    for (const field of objType.fields) {
      if (field.name || field.memo) {
        documents.push({
          id: String(docId++),
          objectType: objType.name,
          fieldName: field.name || field.id,
          type: 'field',
          title: `${objType.name} > ${field.name || field.id}`,
          content: field.memo || '',
          group: objType.group,
        });
      }
    }
  }

  // Index engineering reference content
  for (const doc of engineeringDocs) {
    documents.push({
      id: String(docId++),
      objectType: doc.id,
      type: 'engineering',
      title: doc.title,
      content: doc.content,
      group: doc.category,
    });
  }

  await searchIndex.addAllAsync(documents);
  isInitialized = true;

  console.log(`Search index initialized with ${documents.length} documents`);
  return documents.length;
}

/**
 * Extract a snippet from content around matching query terms
 */
function extractSnippet(content: string, query: string, maxLength = 120): string | undefined {
  if (!content || !query) return undefined;

  // Clean content - remove markdown syntax
  const cleanContent = content
    .replace(/^#+\s+/gm, '') // Remove headings
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic
    .replace(/`([^`]+)`/g, '$1') // Remove code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
    .replace(/\$[^$]+\$/g, '[equation]') // Replace math
    .replace(/<[^>]+>/g, '') // Remove HTML tags
    .replace(/\n+/g, ' ') // Collapse newlines
    .replace(/\s+/g, ' ') // Collapse spaces
    .trim();

  if (!cleanContent) return undefined;

  // Find the first match of any query word
  const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 1);
  let bestMatchIndex = -1;

  for (const word of queryWords) {
    const index = cleanContent.toLowerCase().indexOf(word);
    if (index !== -1 && (bestMatchIndex === -1 || index < bestMatchIndex)) {
      bestMatchIndex = index;
    }
  }

  if (bestMatchIndex === -1) {
    // No match found, return start of content
    return cleanContent.length > maxLength
      ? cleanContent.slice(0, maxLength).trim() + '...'
      : cleanContent;
  }

  // Extract snippet centered around the match
  const snippetStart = Math.max(0, bestMatchIndex - 40);
  const snippetEnd = Math.min(cleanContent.length, bestMatchIndex + maxLength - 40);

  let snippet = cleanContent.slice(snippetStart, snippetEnd);

  // Add ellipsis if truncated
  if (snippetStart > 0) snippet = '...' + snippet;
  if (snippetEnd < cleanContent.length) snippet = snippet + '...';

  return snippet.trim();
}

/**
 * Map a MiniSearch result to our DocsSearchResult type
 */
function mapResult(result: {
  id: string | number;
  score: number;
  [key: string]: unknown;
}, query: string): DocsSearchResult {
  const content = result['content'] ? String(result['content']) : '';

  return {
    id: String(result.id),
    objectType: String(result['objectType'] || ''),
    fieldName: result['fieldName'] ? String(result['fieldName']) : undefined,
    type: (result['type'] || 'object') as 'object' | 'field' | 'engineering' | 'guide',
    title: String(result['title'] || ''),
    group: result['group'] ? String(result['group']) : undefined,
    score: result.score,
    snippet: extractSnippet(content, query),
  };
}

/**
 * Search the documentation
 */
export function searchDocs(query: string, limit = 20): DocsSearchResult[] {
  if (!searchIndex || !query.trim()) return [];

  const results = searchIndex.search(query);

  return results.slice(0, limit).map(r => mapResult(r, query));
}

/**
 * Search with filters
 */
export function searchDocsFiltered(
  query: string,
  options: {
    type?: 'object' | 'field' | 'engineering' | 'guide';
    group?: string;
    limit?: number;
  } = {}
): DocsSearchResult[] {
  if (!searchIndex || !query.trim()) return [];

  const { type, group, limit = 20 } = options;

  const results = searchIndex.search(query, {
    filter: (result: Record<string, unknown>) => {
      if (type && result['type'] !== type) return false;
      if (group && result['group'] !== group) return false;
      return true;
    },
  });

  return results.slice(0, limit).map(r => mapResult(r, query));
}

/**
 * Get suggestions as user types
 */
export function getSuggestions(query: string, limit = 5): string[] {
  if (!searchIndex || !query.trim()) return [];

  const results = searchIndex.autoSuggest(query);
  return results.slice(0, limit).map((r: { suggestion: string }) => r.suggestion);
}

/**
 * Check if the search index is initialized
 */
export function isSearchIndexReady(): boolean {
  return isInitialized && searchIndex !== null;
}

/**
 * Clear the search index
 */
export function clearSearchIndex(): void {
  if (searchIndex) {
    searchIndex.removeAll();
  }
  isInitialized = false;
}
